import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Home as HomeIcon } from 'lucide-react';
import GoogleAuthButton from '../components/GoogleAuthButton';
import MFAVerification from '../components/MFAVerification';
import api from '../lib/api';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, setUser, setToken } = useAuth();
  const navigate = useNavigate();
  
  // MFA state
  const [showMFA, setShowMFA] = useState(false);
  const [mfaSessionToken, setMfaSessionToken] = useState(null);
  const [mfaMethods, setMfaMethods] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Call login API directly to check for MFA
      const response = await api.post('/auth/login', { 
        email, 
        password,
        remember_me: rememberMe,
      });
      const data = response.data;
      
      // Check if MFA is required
      if (data.mfa_required) {
        setMfaSessionToken(data.mfa_session_token);
        setMfaMethods(data.mfa_methods || ['email']);
        setShowMFA(true);
        setLoading(false);
        return;
      }
      
      // No MFA required - proceed with normal login
      if (data.access_token && data.user) {
        // Store token and user (use 'access_token' for consistency with api.js)
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setToken(data.access_token);
        setUser(data.user);
        
        toast.success('Welcome back!');
        navigateByRole(data.user);
      } else {
        toast.error('Login failed - invalid response');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Login failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleMFASuccess = (data) => {
    // Store token and user after MFA verification (use 'access_token' for consistency)
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setToken(data.access_token);
    setUser(data.user);
    
    toast.success('Welcome back!');
    navigateByRole(data.user);
  };

  const navigateByRole = (user) => {
    // Small delay to ensure state is updated
    setTimeout(() => {
      const routes = {
        'tenant': '/tenant/dashboard',
        'landlord': '/landlord/dashboard',
        'landlord_admin': '/landlord/dashboard',
        'super_admin': '/admin/console',
        'support_agent': '/admin/console',
        'admin': '/admin/console',
        'contractor': '/contractor/dashboard'
      };
      
      const targetRoute = routes[user.role] || '/';
      navigate(targetRoute, { replace: true });
    }, 100);
  };

  // Show MFA verification screen
  if (showMFA) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary rounded-lg">
                <HomeIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-display font-extrabold text-primary">LetWise</h1>
            </Link>
          </div>
          
          <MFAVerification
            mfaSessionToken={mfaSessionToken}
            availableMethods={mfaMethods}
            userEmail={email}
            onSuccess={handleMFASuccess}
            onCancel={() => {
              setShowMFA(false);
              setMfaSessionToken(null);
              setMfaMethods([]);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary rounded-lg">
              <HomeIcon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-display font-extrabold text-primary">LetWise</h1>
          </Link>
          <h2 className="text-3xl font-display font-bold text-slate-900 mb-2">Welcome Back</h2>
          <p className="text-slate-600">Sign in to your account</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-slate-700">Password</label>
                <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition"
                placeholder="••••••••"
                required
              />
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-600">
                Remember me for 30 days
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-4 text-lg disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <GoogleAuthButton text="Sign in with Google" />

          <div className="mt-6 text-center">
            <p className="text-slate-600">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-primary font-semibold hover:underline">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}